'use client';

import { Box, Typography } from '@mui/material';

import { Avatar } from '@/components';
import { useT } from '@/hooks';

export interface AvatarSectionProps {
  avatarUrl: string | null;
  userName: string;
  isUploading?: boolean;
  onAvatarUpload?: () => void;
  onFileChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  fileInputRef?: React.RefObject<HTMLInputElement>;
}

export function AvatarSection({
  avatarUrl,
  userName,
  isUploading,
  onAvatarUpload,
  onFileChange,
  fileInputRef,
}: AvatarSectionProps) {
  const { t } = useT();
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 6 }}>
      <Box
        onClick={onAvatarUpload}
        sx={{
          position: 'relative',
          display: 'block',
          p: 0.5,
          borderRadius: '50%',
          cursor: onAvatarUpload ? 'pointer' : 'default',
          background: (theme) =>
            `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          boxShadow: (theme) =>
            theme.palette.mode === 'dark'
              ? '0 10px 30px -10px rgba(0, 0, 0, 0.5)'
              : '0 10px 30px -10px rgba(168, 85, 247, 0.3)',
          transition: 'transform 0.2s ease',
          '&:hover': onAvatarUpload
            ? {
                transform: 'scale(1.05)',
              }
            : {},
        }}
      >
        <Avatar
          src={avatarUrl}
          alt={userName || 'User'}
          size={120}
          isLoading={isUploading}
          sx={{
            width: { xs: 100, sm: 120 },
            height: { xs: 100, sm: 120 },
            borderRadius: '50%',
            border: '4px solid',
            borderColor: 'background.paper',
          }}
        />
        <input
          type="file"
          ref={fileInputRef}
          onChange={onFileChange}
          accept="image/*"
          style={{ display: 'none' }}
        />
      </Box>
      <Box sx={{ mt: 2.5, textAlign: 'center' }}>
        <Typography variant="h5" fontWeight={700} sx={{ color: 'text.primary', mb: 0.5 }}>
          {userName}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', opacity: 0.8 }}>
          {t('profile.personalProfile')}
        </Typography>
      </Box>
    </Box>
  );
}
